import { PRESET_EMOJIS, type EmojiEntry } from '@jarvis/platform-file-review';
import { listEmojiCatalog, type EmojiCatalogItem } from '../../../api/emojiCatalog';

export type EmojiCategoryId =
  | 'faces'
  | 'people'
  | 'activities'
  | 'nature'
  | 'travel'
  | 'objects'
  | 'hearts'
  | 'fantasy';

export type CategorizedEmojiEntry = EmojiEntry & { category: string };

export const EMOJI_CATEGORIES: { id: EmojiCategoryId | 'all'; label: string; shortLabel: string }[] =
  [
    { id: 'all', label: 'All emoji', shortLabel: 'All' },
    { id: 'faces', label: 'Faces', shortLabel: 'Faces' },
    { id: 'people', label: 'People / gestures', shortLabel: 'People' },
    { id: 'activities', label: 'Activities', shortLabel: 'Activities' },
    { id: 'nature', label: 'Nature / weather', shortLabel: 'Nature' },
    { id: 'travel', label: 'Travel / places', shortLabel: 'Travel' },
    { id: 'objects', label: 'Objects / tech', shortLabel: 'Objects' },
    { id: 'hearts', label: 'Hearts / symbols', shortLabel: 'Hearts' },
    { id: 'fantasy', label: 'Animals / fantasy', shortLabel: 'Fantasy' }
  ];

export const EMOJI_CATEGORY_LABELS = new Map<string, string>(
  EMOJI_CATEGORIES.filter((category) => category.id !== 'all').map((category) => [
    category.id,
    category.label
  ])
);

export const EMOJI_EDITOR_CATEGORIES = EMOJI_CATEGORIES.filter(
  (category): category is { id: EmojiCategoryId; label: string; shortLabel: string } =>
    category.id !== 'all'
);

export function categoryFromPresetIndex(index: number): EmojiCategoryId {
  return index < 64
    ? 'faces'
    : index < 109
      ? 'people'
      : index < 150
        ? 'activities'
        : index < 180
          ? 'nature'
          : index < 220
            ? 'travel'
            : index < 279
              ? 'objects'
              : index < 297
                ? 'hearts'
                : 'fantasy';
}

export function fallbackEmojiCatalog(preset: EmojiEntry[] = PRESET_EMOJIS): CategorizedEmojiEntry[] {
  return preset.map((entry, index) => ({
    ...entry,
    category: categoryFromPresetIndex(index)
  }));
}

export function catalogItemToEntry(item: EmojiCatalogItem): CategorizedEmojiEntry {
  return {
    emoji: item.emoji,
    name: item.name,
    search: item.keywords ?? [],
    category: item.category
  };
}

export async function loadEmojiCatalog(): Promise<CategorizedEmojiEntry[]> {
  try {
    const page = await listEmojiCatalog({ take: 2000 });
    if (page.items.length > 0) return page.items.map(catalogItemToEntry);
  } catch {
    /* keep the bundled preset when the catalog API is unavailable */
  }
  return fallbackEmojiCatalog();
}

export function matchesEmoji(entry: CategorizedEmojiEntry, query: string): boolean {
  if (!query) return true;
  const haystack = [
    entry.name,
    EMOJI_CATEGORY_LABELS.get(entry.category) ?? entry.category,
    ...entry.search
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}
